import { ApplicationRef, DOCUMENT, Injectable, effect, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';

/**
 * Détection et application des nouvelles versions livrées.
 *
 * L'application enregistrait un service worker sans jamais interroger
 * `SwUpdate` : un utilisateur ayant déjà ouvert le site continuait de recevoir
 * la version mise en cache, et un déploiement pouvait rester invisible tant
 * qu'il n'avait pas fermé tous ses onglets. C'est exactement le symptôme d'un
 * « toujours l'ancien design » côté navigateur alors que le serveur est à jour.
 *
 * On ne recharge pas de force : sur une application clinique, l'utilisateur
 * peut être au milieu d'une saisie. La disponibilité est signalée, et il
 * décide du moment.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly document = inject(DOCUMENT);

  /** Vrai quand une version est téléchargée et prête à être activée. */
  readonly updateReady = signal(false);

  constructor() {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.updateReady.set(true));

    // Une vérification au démarrage suffirait pour un site vitrine ; ici les
    // postes restent ouverts toute une garde, d'où le contrôle périodique.
    // On attend la stabilisation de l'app pour ne pas concurrencer le premier
    // rendu, puis on interroge toutes les six heures.
    const stable = this.appRef.isStable.pipe(first((isStable) => isStable));
    concat(stable, interval(6 * 60 * 60 * 1000)).subscribe(() => {
      this.swUpdate.checkForUpdate().catch(() => {
        // Hors ligne ou serveur injoignable : on retentera au prochain cycle.
      });
    });

    // Un service worker cassé ne doit pas bloquer l'utilisateur sur une
    // version morte : on repart d'un chargement propre.
    this.swUpdate.unrecoverable.subscribe(() => this.document.location.reload());
  }

  /** Active la version téléchargée et recharge. Déclenché par l'utilisateur. */
  async applyUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) return;
    await this.swUpdate.activateUpdate();
    this.document.location.reload();
  }
}
